import React, { useState, useEffect } from "react";
import {
  Modal,
  View,
  TouchableOpacity,
  TouchableWithoutFeedback,
  ScrollView,
  TextInput,
  Pressable,
  ActivityIndicator,
} from "react-native";
import StyledText from "../../common/ui/styled-text";
import { X, CheckCircle } from "lucide-react-native";
import { ICallOrderItem } from "../../../types/order";
import { displayPrice } from "../../../utils/price-helpers";
import CurrencyInput from "../../ui/currency-input";
import Checkbox from "../../common/ui/checkbox";

interface RefundModalProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (amount: number) => void;
  orderItems: ICallOrderItem[];
  orderTotal: number;
  callerName: string;
  phone: string;
  isLoading?: boolean;
  showSuccess?: boolean;
  error?: string | null;
}

type RefundOption = "total" | "items" | "custom";

const RefundModal: React.FC<RefundModalProps> = ({
  visible,
  onClose,
  onSubmit,
  orderItems,
  orderTotal,
  callerName,
  phone,
  isLoading = false,
  showSuccess = false,
  error = null,
}) => {
  const [selectedOption, setSelectedOption] = useState<RefundOption>("total");
  const [selectedItems, setSelectedItems] = useState<Record<string, number>>(
    {}
  );
  const [customAmount, setCustomAmount] = useState(0);

  const MIN_REFUND_AMOUNT = 1;
  const MAX_REFUND_AMOUNT = orderTotal;

  const handleCustomAmountChange = (value: number) => {
    if (value > MAX_REFUND_AMOUNT) {
      setCustomAmount(MAX_REFUND_AMOUNT);
    } else {
      setCustomAmount(value);
    }
  };

  const itemsRefundAmount = Object.entries(selectedItems).reduce(
    (total, [itemId, quantity]) => {
      const item = orderItems.find((i) => i.id === itemId);
      return total + (item?.price || 0) * quantity;
    },
    0
  );

  const calculateRefundAmount = () => {
    switch (selectedOption) {
      case "total":
        return orderTotal;
      case "items":
        return itemsRefundAmount;
      case "custom":
        return customAmount;
      default:
        return 0;
    }
  };

  const refundAmount = calculateRefundAmount();

  const handleQuantityChange = (itemId: string, newQuantity: number) => {
    const item = orderItems.find((i) => i.id === itemId);
    if (!item) return;
    setSelectedItems({
      ...selectedItems,
      [itemId]: Math.max(1, Math.min(item.quantity, newQuantity)),
    });
  };

  const handleRefundSubmit = () => {
    if (
      refundAmount >= MIN_REFUND_AMOUNT &&
      refundAmount <= MAX_REFUND_AMOUNT
    ) {
      onSubmit(refundAmount);
    }
  };

  const isSubmitDisabled =
    refundAmount < MIN_REFUND_AMOUNT ||
    refundAmount > MAX_REFUND_AMOUNT ||
    isLoading;

  const resetForm = () => {
    setSelectedOption("total");
    setSelectedItems({});
    setCustomAmount(0);
  };

  useEffect(() => {
    if (!visible) {
      resetForm();
    }
  }, [visible]);

  const renderSuccessPage = () => (
    <View className="bg-white rounded-xl p-6 w-[90%] max-w-md h-[600px]">
      <View className="flex-row justify-between items-center mb-4">
        <StyledText className="text-xl font-semibold text-gray-900">
          Refund Submitted
        </StyledText>
        <TouchableOpacity
          onPress={onClose}
          className="p-2 hover:bg-gray-100 rounded-full"
        >
          <X size={20} color="#6b7280" />
        </TouchableOpacity>
      </View>

      <View className="flex-1 justify-center items-center">
        <CheckCircle size={80} color="#10b981" className="mb-6" />
        <StyledText className="text-2xl font-bold text-gray-900 mb-2">
          Refund Submitted
        </StyledText>
        <StyledText className="text-lg text-gray-600 mb-8 text-center">
          Your refund of {displayPrice(refundAmount)} has been successfully
          submitted.
        </StyledText>

        <TouchableOpacity
          onPress={onClose}
          className="bg-green-600 hover:bg-green-700 active:bg-green-700 rounded-md py-3 px-8 w-full"
        >
          <StyledText className="text-white font-medium text-center">
            Close
          </StyledText>
        </TouchableOpacity>
      </View>
    </View>
  );

  const renderLoadingPage = () => (
    <View className="bg-white rounded-xl p-6 w-[90%] max-w-md h-[600px]">
      <View className="flex-row justify-between items-center mb-4">
        <StyledText className="text-xl font-semibold text-gray-900">
          Submitting Refund
        </StyledText>
        <TouchableOpacity
          onPress={onClose}
          className="p-2 hover:bg-gray-100 rounded-full"
        >
          <X size={20} color="#6b7280" />
        </TouchableOpacity>
      </View>

      <View className="flex-1 justify-center items-center">
        <ActivityIndicator size="large" color="#3b82f6" className="mb-6" />
        <StyledText className="text-xl font-semibold text-gray-900 mb-2">
          Submitting Your Refund
        </StyledText>
        <StyledText className="text-base text-gray-600 mb-4 text-center">
          Please wait while we submit your refund of{" "}
          {displayPrice(refundAmount)}.
        </StyledText>
        <StyledText className="text-sm text-gray-400 text-center">
          This may take a few moments...
        </StyledText>
      </View>
    </View>
  );

  const renderErrorPage = () => (
    <View className="bg-white rounded-xl p-6 w-[90%] max-w-md h-[600px]">
      <View className="flex-row justify-between items-center mb-4">
        <StyledText className="text-xl font-semibold text-gray-900">
          Refund Failed
        </StyledText>
        <TouchableOpacity
          onPress={onClose}
          className="p-2 hover:bg-gray-100 rounded-full"
        >
          <X size={20} color="#6b7280" />
        </TouchableOpacity>
      </View>

      <View className="flex-1 justify-center items-center">
        <View className="w-16 h-16 bg-red-100 rounded-full items-center justify-center mb-6">
          <X size={32} color="#ef4444" />
        </View>
        <StyledText className="text-2xl font-bold text-gray-900 mb-2">
          Refund Failed
        </StyledText>
        <StyledText className="text-base text-gray-600 mb-6 text-center select-text">
          We encountered an error while processing the customer's refund of{" "}
          {displayPrice(refundAmount)}.
        </StyledText>

        <View className="flex space-y-3 w-full">
          <TouchableOpacity
            onPress={onClose}
            className="border border-gray-300 bg-white hover:bg-gray-50 active:bg-gray-100 rounded-md py-3 px-6"
          >
            <StyledText className="text-gray-700 font-medium text-center">
              Close
            </StyledText>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );

  const renderMainContent = () => (
    <View className="bg-white rounded-xl p-6 w-[90%] max-w-md h-[620px]">
      <View className="flex-row justify-between items-center mb-4">
        <StyledText className="text-xl font-semibold text-gray-900">
          Refund Charges
        </StyledText>
        <TouchableOpacity
          onPress={onClose}
          className="p-2 hover:bg-gray-100 rounded-full"
        >
          <X size={20} color="#6b7280" />
        </TouchableOpacity>
      </View>

      <ScrollView
        className="flex-1"
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{
          gap: 12,
          padding: 8,
        }}
      >
        <TouchableOpacity
          onPress={() => setSelectedOption("total")}
          className={`rounded-lg border border-gray-200 transition-all ${
            selectedOption === "total"
              ? "ring-2 ring-blue-500 bg-blue-50"
              : "bg-white active:bg-gray-50"
          }`}
          disabled={selectedOption === "total"}
          activeOpacity={0.7}
        >
          <View className="p-3">
            <View className="flex-row items-center space-x-3">
              <View className="flex-1">
                <StyledText className="text-base font-medium text-gray-900">
                  Order Total
                </StyledText>
                <StyledText className="text-sm text-gray-600 mt-1">
                  Refund the full order amount
                </StyledText>
              </View>
              <StyledText
                className={`text-lg font-semibold ${
                  selectedOption === "total" ? "text-blue-600" : "text-gray-600"
                }`}
              >
                {displayPrice(orderTotal)}
              </StyledText>
            </View>
          </View>
        </TouchableOpacity>

        <TouchableOpacity
          onPress={() => setSelectedOption("items")}
          className={`rounded-lg border border-gray-200 transition-all ${
            selectedOption === "items"
              ? "ring-2 ring-blue-500 bg-blue-50"
              : "bg-white active:bg-gray-50"
          }`}
          disabled={selectedOption === "items"}
          activeOpacity={0.7}
        >
          <View className="p-3">
            <View className="flex-row items-center space-x-3">
              <View className="flex-1">
                <StyledText className="text-base font-medium text-gray-900">
                  Select Items
                </StyledText>
                <StyledText className="text-sm text-gray-600 mt-1">
                  Refund specific items
                </StyledText>
              </View>
              <StyledText
                className={`text-lg font-semibold ${
                  selectedOption === "items" ? "text-blue-600" : "text-gray-600"
                }`}
              >
                {displayPrice(itemsRefundAmount)}
              </StyledText>
            </View>
            {selectedOption === "items" && (
              <div className="mt-3 space-y-3">
                {orderItems.map((item) => {
                  const selectedQuantity = selectedItems[item.id] || 0;
                  const isSelected = selectedQuantity > 0;

                  return (
                    <div key={item.id} className="flex flex-col">
                      <TouchableOpacity
                        onPress={() => {
                          if (isSelected) {
                            const { [item.id]: _, ...rest } = selectedItems;
                            setSelectedItems(rest);
                          } else {
                            setSelectedItems({
                              ...selectedItems,
                              [item.id]: item.quantity,
                            });
                          }
                        }}
                        className={`flex flex-col p-3 rounded-lg border transition-colors bg-white border-gray-200 active:bg-gray-50`}
                        activeOpacity={0.7}
                      >
                        <div className="flex justify-between items-center">
                          <div className="flex items-center space-x-3">
                            <Checkbox
                              checked={isSelected}
                              onToggle={(checked) => {
                                if (checked) {
                                  setSelectedItems({
                                    ...selectedItems,
                                    [item.id]: item.quantity,
                                  });
                                } else {
                                  const { [item.id]: _, ...rest } =
                                    selectedItems;
                                  setSelectedItems(rest);
                                }
                              }}
                              label={item.name}
                              labelClassName="font-medium"
                            />
                          </div>

                          <span className="text-sm font-medium ml-2">
                            {displayPrice(item.price)}
                          </span>
                        </div>
                      </TouchableOpacity>

                      {isSelected && item.quantity > 1 && (
                        <div className="mt-2 mb-1 ml-2 flex items-center gap-2">
                          <span className="text-sm text-gray-600">Qty:</span>
                          <div className="flex items-center gap-1">
                            <Pressable
                              className={`w-6 h-6 rounded-full p-0 bg-white border border-gray-300 justify-center items-center ${
                                selectedQuantity <= 1
                                  ? "opacity-30 cursor-not-allowed"
                                  : ""
                              }`}
                              onPress={() =>
                                handleQuantityChange(
                                  item.id,
                                  selectedQuantity - 1
                                )
                              }
                              disabled={selectedQuantity <= 1}
                            >
                              <span className="text-xs">−</span>
                            </Pressable>

                            <span className="w-6 text-center text-sm font-medium">
                              {selectedQuantity}
                            </span>

                            <Pressable
                              className={`w-6 h-6 rounded-full p-0 bg-white border border-gray-300 justify-center items-center ${
                                selectedQuantity >= item.quantity
                                  ? "opacity-30 cursor-not-allowed"
                                  : ""
                              }`}
                              onPress={() =>
                                handleQuantityChange(
                                  item.id,
                                  selectedQuantity + 1
                                )
                              }
                              disabled={selectedQuantity >= item.quantity}
                            >
                              <span className="text-xs">+</span>
                            </Pressable>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </View>
        </TouchableOpacity>

        <TouchableOpacity
          onPress={() => setSelectedOption("custom")}
          className={`rounded-lg border border-gray-200 transition-all ${
            selectedOption === "custom"
              ? "ring-2 ring-blue-500 bg-blue-50"
              : "bg-white active:bg-gray-50"
          }`}
          disabled={selectedOption === "custom"}
          activeOpacity={0.7}
        >
          <View className="p-3">
            <View className="flex-row items-center space-x-3">
              <View className="flex-1">
                <StyledText className="text-base font-medium text-gray-900">
                  Custom Amount
                </StyledText>
                <StyledText className="text-sm text-gray-600 mt-1">
                  Refund a specific dollar amount
                </StyledText>
              </View>
              <StyledText
                className={`text-lg font-semibold ${
                  selectedOption === "custom"
                    ? "text-blue-600"
                    : "text-gray-600"
                }`}
              >
                {displayPrice(customAmount)}
              </StyledText>
            </View>
            {selectedOption === "custom" && (
              <CurrencyInput
                value={customAmount}
                onChangeValue={handleCustomAmountChange}
                maxAmount={MAX_REFUND_AMOUNT}
              />
            )}
          </View>
        </TouchableOpacity>
      </ScrollView>

      <div className="sticky bottom-0 bg-white pt-4 mt-4 border-t">
        <div className="flex items-center justify-between mb-4">
          <div className="text-lg select-text">
            <span className="text-gray-600">Refunding:</span>
            <span className="font-bold text-2xl text-blue-600 ml-2">
              {displayPrice(refundAmount)}
            </span>
            {callerName?.length > 0 && (
              <span className="text-gray-600 ml-2.5">to {callerName}</span>
            )}
          </div>
        </div>

        <div className="flex space-x-3">
          <TouchableOpacity
            onPress={onClose}
            className="border border-gray-300 bg-white hover:bg-gray-50 active:bg-gray-100 rounded-md py-2 px-4 flex-row items-center justify-center space-x-2 transition-all duration-200 flex-1"
          >
            <StyledText>Cancel</StyledText>
          </TouchableOpacity>
          <TouchableOpacity
            onPress={handleRefundSubmit}
            disabled={isSubmitDisabled}
            className={`bg-red-600 hover:bg-red-700 active:bg-red-700 rounded-md py-2 px-4 flex-row items-center justify-center space-x-2 transition-all duration-200 flex-1 ${
              !!isSubmitDisabled && "opacity-50 cursor-not-allowed"
            }`}
          >
            <StyledText className="text-white font-medium">
              Refund {displayPrice(refundAmount)}
            </StyledText>
          </TouchableOpacity>
        </div>
      </div>
    </View>
  );

  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType="none"
      onRequestClose={onClose}
    >
      <TouchableWithoutFeedback onPress={onClose}>
        <View className="flex-1 bg-black/50 justify-center items-center">
          <TouchableWithoutFeedback>
            {showSuccess
              ? renderSuccessPage()
              : isLoading
                ? renderLoadingPage()
                : error
                  ? renderErrorPage()
                  : renderMainContent()}
          </TouchableWithoutFeedback>
        </View>
      </TouchableWithoutFeedback>
    </Modal>
  );
};

export default RefundModal;
